import { Component } from 'preact'
import axios from 'axios'
import qs from 'qs'

import { ContentType } from '@insided/shared/utils/contentType'

import { ConfigContext, getFormattedContent, getFormattedTitle, showEscalateButton } from '../../utils'
import { AlertMessage, EscalateButton } from '../EscalationButtons'
import CreateEscalationModal from '../CreateEscalationModal'

/* eslint-disable import/no-internal-modules */
import tracker from '../../../../../../components/tracker/publish'
import { publish } from '@insided/deprecated-utils/globals'

interface Props {
  isEnabled: boolean
  title?: string
  username?: string
  userId: string
  content?: string | null
  publicId: number
  moderatorId: string
  isReply: boolean
  contentType: ContentType
  isTrashed: boolean
  destinationUrl: string
  topicId: number
}

interface State {
  showModal: boolean
  url: string | null
  isLoading: boolean
  caseContent: string | null
  caseSubject?: string
  subjectValidationError?: string
  contentValidationError?: string
  messageType?: string|null
  message?: string
}

class CreateSalesforceCaseButton extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      showModal: false,
      url: null,
      isLoading: false,
      caseSubject: getFormattedTitle(this.props.title, this.props.isReply),
      caseContent: getFormattedContent(this.props.contentType, this.props.destinationUrl, this.props.topicId, this.props.content)
    }
    this.fetchCaseUrl = this.fetchCaseUrl.bind(this)
    this.createCase = this.createCase.bind(this)
    this.closeModal = this.closeModal.bind(this)
  }

  async componentDidMount () {
    if (this.props.isEnabled) {
      await this.fetchCaseUrl()
    }
  }

  componentDidUpdate (previousProps: Readonly<Props>) {
    if (this.props.isTrashed !== previousProps.isTrashed) {
      this.setState({ messageType: null })
    }

    if (this.props.title !== previousProps.title) {
      this.setState({
        caseSubject: getFormattedTitle(this.props.title, this.props.isReply)
      })
    }

    if (this.props.content !== previousProps.content) {
      this.setState({
        caseContent: this.props.content ?? null
      })
    }
  }

  async fetchCaseUrl () {
    this.setState({ isLoading: true })
    try {
      const data = {
        postId: this.props.publicId,
        isReply: this.props.isReply,
        contentType: this.props.contentType,
        type: 'salesforce'
      }
      const result = await axios.get('/escalation/fetch', { params: data })
      const ticketUrl = result.data.ticketUrl
      this.setState({ url: ticketUrl, isLoading: false })
      const isReplyToString = this.props.isReply.toString()
      const ticketId = `${this.props.publicId}:${this.props.contentType}:${isReplyToString}`
      publish('salesforce_case_created', { url: ticketUrl, id: ticketId })
    } catch (e) {
      this.setState({ url: null, isLoading: false })
    }
  }

  async createCase () {
    this.setState({ messageType: null, subjectValidationError: undefined, contentValidationError: undefined, isLoading: true })
    const { caseSubject, caseContent } = this.state
    const formattedSubject = caseSubject?.trim()
    const formattedContent = caseContent?.trim()
    if (formattedSubject === '' && formattedContent === '') {
      this.setState({
        contentValidationError: 'Please fill in the required field.',
        subjectValidationError: 'Please fill in the required field.',
        isLoading: false
      })
      return
    }
    if (formattedContent === '') {
      this.setState({ contentValidationError: 'Please fill in the required field.', isLoading: false })
      return
    }

    if (formattedSubject === '') {
      this.setState({ subjectValidationError: 'Please fill in the required field.', isLoading: false })
      return
    }

    try {
      const { moderatorId, publicId, isReply, username, userId, contentType } = this.props
      const params = {
        moderatorId: moderatorId,
        postId: publicId,
        isReply: isReply,
        contentType: contentType,
        type: 'salesforce',
        title: getFormattedTitle(formattedSubject),
        content: formattedContent,
        authorUsername: username,
        authorUserId: userId
      }
      const result = await axios.post('/escalation/add', qs.stringify(params), { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
      const ticketUrl = result.data.ticketUrl
      this.setState({
        url: result.data.ticketUrl,
        showModal: false,
        messageType: 'success',
        message: 'You’ve created a Salesforce case.',
        isLoading: false
      })
      const isReplyToString = this.props.isReply.toString()
      const id = `${this.props.publicId}:${this.props.contentType}:${isReplyToString}`
      publish('salesforce_case_created', { url: ticketUrl, id: id })
      tracker.trackEvent('userevent', {
        type: 'Post Escalated',
        escalated_to: 'Salesforce',
        id: publicId,
        topic_url: window.location.href,
        topic_content_type: contentType,
        is_opening_post: !isReply
      })
    } catch (e) {
      this.setState({
        url: null,
        showModal: false,
        messageType: 'error',
        message: e.response.data.error ?? 'Something went wrong, please try again.',
        isLoading: false
      })
    }
  }

  closeModal () {
    this.setState({
      showModal: false,
      caseContent: getFormattedContent(this.props.contentType, this.props.destinationUrl, this.props.topicId, this.props.content),
      caseSubject: getFormattedTitle(this.props.title, this.props.isReply),
      contentValidationError: undefined,
      subjectValidationError: undefined
    })
  }

  render () {
    const { isEnabled, username, isReply, contentType } = this.props
    const { isLoading, showModal, messageType, message, url, caseSubject, caseContent, subjectValidationError, contentValidationError } = this.state

    if (!isEnabled) {
      return null
    }

    return (
      <ConfigContext.Consumer>
        {({ moderationConfig: { escalate, escalateReply } }) => {
          const showEscalationButton = showEscalateButton(escalate, escalateReply, isReply, Boolean(url), contentType)
          return (
            <div className='integration__escalate qa-escalate-salesforce'>
              <AlertMessage messageType={messageType} message={message} />
              {showEscalationButton &&
                <div>
                  <EscalateButton
                    isDisabled={isLoading}
                    tooltipMessage='Create a Salesforce case'
                    imageName='salesforce-xs'
                    onClick={() => this.setState({ showModal: true, messageType: null })}
                    customClasses='qa-create-salesforce-case'
                  />
                  <CreateEscalationModal
                    modalTitle='Create a Salesforce case'
                    modalTitleImage='salesforce'
                    isOpen={showModal}
                    closeModal={() => this.closeModal()}
                    title={caseSubject}
                    username={username}
                    content={caseContent}
                    onChangeCallback={(value: string) => this.setState({ caseSubject: value })}
                    onContentChange={(value: string) => this.setState({ caseContent: value })}
                    onSubmit={async () => await this.createCase()}
                    titleValidationError={subjectValidationError}
                    contentValidationError={contentValidationError}
                    onTextareaFocus={() => this.setState({ contentValidationError: undefined })}
                    onClickCallback={() => this.setState({ subjectValidationError: undefined })}
                    isLoading={isLoading}
                  />
                </div>}
            </div>
          )
        }}
      </ConfigContext.Consumer>
    )
  }
}

export default CreateSalesforceCaseButton
